import { QueryCache, QueryClient } from '@tanstack/react-query'

import { isServerQueryError, isTransientQueryError } from './resultQuery'

import { ILogger } from '@/infra/logger/ILogger'

/**
 * Política de retry do React Query.
 *
 * Um dono por classe de falha, para os dois mecanismos não multiplicarem tentativas:
 * - flap de rede / timeout → **transporte** (`AxiosHttpClient`), 3 tentativas com jitter;
 * - 5xx do servidor → **aqui**, 1 retentativa;
 * - 4xx, erro de negócio e abort → ninguém retenta.
 *
 * Se uma query definir `retry` localmente, o default v5 (`retry: 3`) volta a valer e o total vira
 * 3 × 4 = 12 requisições, o que *piora* o problema que a camada de transporte resolve. Nenhuma query
 * deve definir `retry`.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isTransientQueryError(error)) return false
  if (isServerQueryError(error)) return failureCount < 1

  return false
}

/** Backoff com jitter, para N queries que falharam no mesmo evento não retentarem em uníssono. */
export function jitteredRetryDelay(attemptIndex: number): number {
  const ceiling = Math.min(1000 * 2 ** attemptIndex, 8000)

  return ceiling * (0.5 + Math.random() * 0.5)
}

/**
 * Cria o QueryClient. É factory, não singleton de módulo, para os testes não compartilharem cache.
 */
export function createQueryClient(deps: { logger?: ILogger } = {}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        deps.logger?.warn('QueryClient', `query ${JSON.stringify(query.queryKey)} falhou: ${String(error)}`)
      }
    }),
    defaultOptions: {
      queries: {
        // O piloto abre e fecha o drawer dezenas de vezes numa sessão de planejamento. O default do
        // v5 (`staleTime: 0`) refaria rede em cada abertura — pior que o comportamento atual.
        staleTime: 60_000,
        // Telas do drawer entram e saem da árvore constantemente; 5 min (default) descartaria cache
        // ainda útil.
        gcTime: 30 * 60_000,
        retry: shouldRetryQuery,
        retryDelay: jitteredRetryDelay,
        // Desligado GLOBALMENTE de propósito: o app convive com mapa, PDF, popovers e devtools, e o
        // churn de foco é altíssimo. Refetch em foco geraria rajadas de rede — exatamente o que
        // amplifica o ERR_NETWORK_CHANGED. Ligado por domínio onde faz sentido.
        refetchOnWindowFocus: false,
        // Rede de segurança para queda REAL de conectividade. Atenção: isto NÃO cobre o
        // ERR_NETWORK_CHANGED, que não altera `navigator.onLine` nem dispara `online` — esse caso é
        // resolvido pelo retry de transporte.
        refetchOnReconnect: true,
        refetchOnMount: true,
        // Erro de query não é crash de render: as views já têm `hasLoadError`/`onRetry`, e o
        // AppErrorBoundary deve continuar servindo só para crash de verdade.
        throwOnError: false
      },
      mutations: {
        retry: 0
      }
    }
  })
}
